import { useCallback, useEffect, useMemo, useState, Dispatch, SetStateAction } from 'react';
import { useSelector } from 'react-redux';
import * as uuid from 'uuid';
import {
  AddInputArgs,
  AddMediaInputArgs,
  AddMMMOfflineInputArgs,
  MediaEntry,
  MediaTable,
  OfflineInputsTable,
  OfflineInputTabs
} from '@blisspointmedia/bpm-types/dist/OfflineInputs';
import {
  getSegmentName,
  getSegmentValue,
  CustomSegmentsData
} from '@Pages/SegmentationMapping/segmentationMappingUtils';
import { SetError } from '../../../redux/modals';
import * as UserRedux from '../../../redux/user';
import { awaitJSON, SegmentationMappingLambdaFetch } from '../../../utils/fetch-utils';
import { AddMediaInputModal } from './AddMediaInputModal';
import { InputContainer } from './InputContainer';
import { InputTable, getTablesContainerInnerWidth } from './InputTable';
import {
  CommitSuccessDialog,
  DiscardConfirmationDialog,
  InstructionsDialog,
  ValidationErrorDialog
} from './OfflineInputsDialogs';
import {
  assignRowIdsToMediaTables,
  buildBlankDmaEntryRow,
  EntryEditsMap,
  fetchValidDmaCodes,
  hasRowId,
  MediaEntryWithRowId,
  NewTableEntriesMap,
  prepCreateTableReq,
  prepDeleteTableReq,
  QueuedRequest,
  RequiredSegmentIds,
  TableEditsMap,
  TableMetadataEditsMap,
  scrollToRow,
  tabularOfflineInputsDownload,
  validateAndUpdateTables as validateAndUpdateTablesInternal
} from './OfflineInputsUtils';

interface MediaTableHeaderLabelsProps {
  table: MediaTable;
  customSegments?: CustomSegmentsData[];
}

// Renders the per-table header block: channel/platform names plus any custom
// segment name/value pairs and the additional description.
const MediaTableHeaderLabels = ({ table, customSegments }: MediaTableHeaderLabelsProps) => {
  const { channelId, platformId, segmentMap, additionalDescription } = table;
  const segments = customSegments || [];
  const channel = getSegmentValue(channelId, segments) || '';
  const platform = getSegmentValue(platformId, segments) || '';

  const customSegmentsLine = segmentMap
    ? Object.keys(segmentMap)
        .filter(
          segmentId =>
            segmentId !== RequiredSegmentIds.CHANNEL && segmentId !== RequiredSegmentIds.PLATFORM
        )
        .map(segmentId => {
          const segmentName = getSegmentName(Number.parseInt(segmentId, 10), segments);
          const segmentValueName = getSegmentValue(segmentMap[segmentId], segments);
          return `${segmentName}: ${segmentValueName}\n`;
        })
        .join('')
    : '';

  return (
    <div className='header-labels'>
      <div>
        <strong>{channel}</strong>
      </div>
      <div>Platform: {platform}</div>
      {customSegmentsLine && <div>{customSegmentsLine}</div>}
      {additionalDescription && <div>{additionalDescription}</div>}
    </div>
  );
};

interface MediaInputsProps {
  inputType: keyof typeof OfflineInputTabs;
  originalTables: MediaTable[];
  updated: { by?: string; at?: string };
  fetching: boolean;
  fetchFailed: boolean;
  refetch: () => void;
  editingUnlocked: boolean;
  setEditingUnlocked: Dispatch<SetStateAction<boolean>>;
  hasUnsavedChanges: boolean;
  setHasUnsavedChanges: Dispatch<SetStateAction<boolean>>;
  dispatchStateReset: boolean;
  setDispatchStateReset: Dispatch<SetStateAction<boolean>>;
  company: string;
  setError: SetError;
}

export const MediaInputs = ({
  inputType,
  originalTables,
  updated,
  fetching,
  fetchFailed,
  refetch,
  editingUnlocked,
  setEditingUnlocked,
  hasUnsavedChanges,
  setHasUnsavedChanges,
  dispatchStateReset,
  setDispatchStateReset,
  company,
  setError
}: MediaInputsProps) => {
  const [tables, setTables] = useState<MediaTable[]>(
    assignRowIdsToMediaTables([...originalTables])
  );
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [tablesInEditMode, setTablesInEditMode] = useState<string[]>([]);
  const [newTableEntries, setNewTableEntries] = useState<NewTableEntriesMap>({});
  const [validationErrors, setValidationErrors] = useState<{ tableId: string; errors: string[] }[]>(
    []
  );
  const [queuedRequests, setQueuedRequests] = useState<QueuedRequest[]>([]);
  const [deletedTables, setDeletedTables] = useState<MediaTable[]>([]);
  const [showAddMediaInputModal, setShowAddMediaInputModal] = useState(false);
  const [editingSettingsTableId, setEditingSettingsTableId] = useState<string | null>(null);
  const [tableMetadataEdits, setTableMetadataEdits] = useState<TableMetadataEditsMap>({});
  const [showCommitSuccessDialog, setShowCommitSuccessDialog] = useState(false);
  const [showInstructionsDialog, setShowInstructionsDialog] = useState(false);
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const [tableEdits, setTableEdits] = useState<TableEditsMap>({});
  const [filter, setFilter] = useState<{ filter: (line: Record<string, any>) => boolean }>();
  const userFullName: string = useSelector(UserRedux.fullNameSelector) ?? '';
  const [customSegments, setCustomSegments] = useState<CustomSegmentsData[] | undefined>();
  const [validDmaCodes, setValidDmaCodes] = useState<Set<string>>(new Set());

  const startEditingTable = (tableId: string, blanks: MediaEntry[]) => {
    setTablesInEditMode([...tablesInEditMode, tableId]);
    setTableEdits({ ...tableEdits, [tableId]: {} });
    setNewTableEntries({ ...newTableEntries, [tableId]: blanks });

    if (blanks.length > 0) {
      scrollToRow(tableId, blanks[blanks.length - 1].date);
    }
  };

  const resetState = useCallback(() => {
    setTables([...originalTables]);
    setSubmitting(false);
    setEditingUnlocked(false);
    setTablesInEditMode([]);
    setHasUnsavedChanges(false);
    setNewTableEntries({});
    setValidationErrors([]);
    setQueuedRequests([]);
    setDeletedTables([]);
    setShowAddMediaInputModal(false);
    setShowCommitSuccessDialog(false);
    setShowInstructionsDialog(false);
    setShowDiscardDialog(false);
    setTableEdits({});
    setEditingSettingsTableId(null);
    setTableMetadataEdits({});
  }, [originalTables, setEditingUnlocked, setHasUnsavedChanges]);

  useEffect(() => {
    if (dispatchStateReset) {
      resetState();
      setDispatchStateReset(false);
    }
  }, [dispatchStateReset, resetState, setDispatchStateReset]);

  useEffect(() => {
    setTables(assignRowIdsToMediaTables([...originalTables]));
  }, [originalTables]);

  useEffect(() => {
    setHasUnsavedChanges(
      Boolean(tablesInEditMode.length) ||
        Boolean(deletedTables.length) ||
        Object.keys(tableMetadataEdits).length > 0
    );
  }, [tablesInEditMode, deletedTables, tableMetadataEdits, setHasUnsavedChanges]);

  useEffect(() => {
    if (!customSegments && company) {
      (async () => {
        try {
          const res = await SegmentationMappingLambdaFetch('/getCustomSegments', {
            params: { company }
          });
          const data = await awaitJSON<CustomSegmentsData[]>(res);
          setCustomSegments(data);
        } catch (e) {
          setError({ message: e.message, reportError: e });
        }
      })();
    }
  }, [company, customSegments, setError]);

  useEffect(() => {
    fetchValidDmaCodes(setError).then(setValidDmaCodes);
  }, [setError]);

  const onCommitSuccess = () => {
    refetch();
    resetState();
    setShowCommitSuccessDialog(true);
  };

  const validateAndUpdateTables = async () => {
    setSubmitting(true);
    await validateAndUpdateTablesInternal(
      company,
      userFullName,
      inputType,
      tableEdits,
      newTableEntries,
      setValidationErrors,
      setError,
      onCommitSuccess,
      queuedRequests,
      setQueuedRequests,
      tableMetadataEdits,
      tables,
      validDmaCodes
    );
    setSubmitting(false);
  };

  const deleteTable = (tableId: string) => {
    prepDeleteTableReq<OfflineInputsTable, TableEditsMap, NewTableEntriesMap>(
      tableId,
      company,
      userFullName,
      inputType,
      tables,
      setTables as Dispatch<SetStateAction<OfflineInputsTable[]>>,
      deletedTables,
      setDeletedTables as Dispatch<SetStateAction<OfflineInputsTable[]>>,
      queuedRequests,
      setQueuedRequests,
      newTableEntries,
      setNewTableEntries,
      tableEdits,
      setTableEdits
    );
  };

  const addBlankDmaRow = (tableId: string) => {
    // Idempotent: React 19 StrictMode double-invokes effects within the same commit, and both
    // invocations would otherwise see the same state and both append a row. Checking inside
    // the updater (rather than relying on the caller's read of stale state) guarantees at most
    // one blank capture row regardless of how many times this is called back-to-back.
    //
    // Edit-aware: a raw newEntries row's `date` never changes in place — typing/pasting into
    // the capture row only ever records an edit in `tableEdits[tableId][rowId]` (see
    // buildInputHeader's onChange in InputTable.tsx). So this must check the edit-merged date,
    // matching what InputTable's self-healing effect checks (mergedEntries) — otherwise, once
    // the row is filled in, this guard would still see the raw `date: ''` and forever refuse
    // to add a new blank row, permanently capping the table at one capture row.
    setNewTableEntries(prev => {
      const existing = prev[tableId] ?? [];
      const hasBlankRow = existing.some(entry => {
        if (!hasRowId(entry)) {
          return false;
        }
        const edited = tableEdits[tableId]?.[entry.rowId];
        return (edited?.date ?? entry.date) === '';
      });
      if (hasBlankRow) {
        return prev;
      }
      return { ...prev, [tableId]: [...existing, buildBlankDmaEntryRow()] };
    });
  };

  const addPastedDmaRows = (tableId: string, rows: MediaEntryWithRowId[]) => {
    setNewTableEntries(prev => ({
      ...prev,
      [tableId]: [...(prev[tableId] ?? []), ...rows]
    }));
    setTableEdits(prev => ({
      ...prev,
      [tableId]: {
        ...(prev[tableId] ?? {}),
        ...Object.fromEntries(rows.map(row => [row.rowId, row]))
      }
    }));
  };

  const deleteUncommittedEntry = (tableId: string, rowId: string) => {
    setNewTableEntries(prev => ({
      ...prev,
      [tableId]: (prev[tableId] ?? []).filter(entry => !hasRowId(entry) || entry.rowId !== rowId)
    }));
    setTableEdits(prev => {
      if (!prev[tableId] || !(rowId in prev[tableId])) {
        return prev;
      }
      const { [rowId]: _removed, ...rest } = prev[tableId];
      return { ...prev, [tableId]: rest };
    });
  };

  const openEditSettingsModal = useCallback((tableId: string) => {
    setEditingSettingsTableId(tableId);
    setShowAddMediaInputModal(true);
  }, []);

  const applyEditTableSettings = useCallback(
    (input: {
      tableId: string;
      segmentMap: Record<string, number>;
      additionalDescription?: string;
      hasDma?: boolean;
    }) => {
      const { tableId, segmentMap, additionalDescription, hasDma } = input;

      // If the table is still a queued CREATE, mutate the queued body in place
      // rather than tracking a separate metadata edit.
      const queuedCreateIndex = queuedRequests.findIndex(req => {
        if (req.type !== 'CREATE') {
          return false;
        }
        const createArgs = (req.body as AddMMMOfflineInputArgs).args as AddInputArgs & {
          segmentMap?: Record<string, number>;
        };
        return createArgs.tableId === tableId;
      });

      if (queuedCreateIndex >= 0) {
        setQueuedRequests(
          queuedRequests.map((req, index) => {
            if (index !== queuedCreateIndex) {
              return req;
            }
            const createBody = req.body as AddMMMOfflineInputArgs;
            const prevArgs = createBody.args as AddInputArgs & {
              channelId: number;
              platformId: number;
              segmentMap?: Record<string, number>;
              additionalDescription?: string;
            };
            const nextArgs: AddMediaInputArgs = {
              ...prevArgs,
              segmentMap: { ...segmentMap },
              channelId: segmentMap[RequiredSegmentIds.CHANNEL] ?? prevArgs.channelId,
              platformId: segmentMap[RequiredSegmentIds.PLATFORM] ?? prevArgs.platformId,
              additionalDescription,
              ...(hasDma !== undefined ? { hasDma } : {})
            } as AddMediaInputArgs;
            return { ...req, body: { ...createBody, args: nextArgs } };
          })
        );
      } else {
        setTableMetadataEdits({
          ...tableMetadataEdits,
          [tableId]: { segmentMap: { ...segmentMap }, additionalDescription, hasDma }
        });
      }

      // Reflect the change immediately in the local tables array so headerLabels
      // re-renders with the new custom-segment names.
      setTables(prev =>
        prev.map(t =>
          t.tableId === tableId
            ? {
                ...t,
                segmentMap: { ...segmentMap },
                channelId: segmentMap[RequiredSegmentIds.CHANNEL] ?? t.channelId,
                platformId: segmentMap[RequiredSegmentIds.PLATFORM] ?? t.platformId,
                additionalDescription,
                hasDma: hasDma ?? t.hasDma
              }
            : t
        )
      );
      setEditingSettingsTableId(null);
    },
    [queuedRequests, tableMetadataEdits]
  );

  const addModal = useMemo(() => {
    const prepCreateMediaTable = (input: {
      channelId: number;
      platformId: number;
      segmentMap: Record<string, number>;
      hasImpressions: boolean;
      hasRedemptions: boolean;
      hasDma: boolean;
      isPaid: boolean;
      otherUnitLabel?: string;
      additionalDescription?: string;
      startDate: Date;
    }) => {
      const {
        channelId,
        platformId,
        segmentMap,
        hasImpressions,
        hasRedemptions,
        hasDma,
        isPaid,
        otherUnitLabel,
        additionalDescription,
        startDate
      } = input;

      const newTable = {
        channelId,
        platformId,
        segmentMap,
        hasImpressions,
        hasRedemptions,
        hasDma,
        isPaid,
        otherUnitLabel,
        additionalDescription,
        tableId: uuid.v4(),
        entries: []
      } as MediaTable & { segmentMap: Record<string, number> };

      prepCreateTableReq<OfflineInputsTable, TableEditsMap, NewTableEntriesMap>({
        company,
        updatedBy: userFullName,
        inputType,
        startDate,
        newTable,
        queuedRequests,
        setQueuedRequests,
        tables,
        setTables: setTables as Dispatch<SetStateAction<OfflineInputsTable[]>>,
        tablesInEditMode,
        setTablesInEditMode,
        newTableEntries,
        setNewTableEntries,
        tableEdits,
        setTableEdits
      });
    };

    const existingTable = editingSettingsTableId
      ? tables.find(t => t.tableId === editingSettingsTableId)
      : undefined;

    return (
      <AddMediaInputModal
        show={showAddMediaInputModal}
        handleClose={() => {
          setShowAddMediaInputModal(false);
          setEditingSettingsTableId(null);
        }}
        createTable={input => {
          prepCreateMediaTable(input);
          setEditingUnlocked(true);
        }}
        isPaid={OfflineInputTabs[inputType] === OfflineInputTabs.PAID_MEDIA_INPUTS}
        customSegments={customSegments}
        mode={editingSettingsTableId ? 'edit' : 'create'}
        existingTable={existingTable}
        editTable={applyEditTableSettings}
      />
    );
  }, [
    company,
    inputType,
    newTableEntries,
    queuedRequests,
    setEditingUnlocked,
    showAddMediaInputModal,
    tableEdits,
    tables,
    tablesInEditMode,
    userFullName,
    customSegments,
    editingSettingsTableId,
    applyEditTableSettings
  ]);

  const download = useCallback(
    () => tabularOfflineInputsDownload(inputType, tables, customSegments || []),
    [inputType, tables, customSegments]
  );

  return (
    <InputContainer
      dialogs={
        <>
          {addModal}
          <CommitSuccessDialog
            show={showCommitSuccessDialog}
            onHide={() => setShowCommitSuccessDialog(false)}
          />
          <InstructionsDialog
            show={showInstructionsDialog}
            onHide={() => {
              setShowInstructionsDialog(false);
            }}
          />
          <DiscardConfirmationDialog
            show={showDiscardDialog}
            onDiscard={() => resetState()}
            onHide={() => setShowDiscardDialog(false)}
          />
          {validationErrors.length > 0 && (
            <ValidationErrorDialog
              errorDates={validationErrors[0].errors}
              onResolve={() => {
                const remainingErrors = validationErrors.slice(1);
                setValidationErrors(remainingErrors);

                if (remainingErrors.length === 0) {
                  validateAndUpdateTables();
                }
              }}
              onReject={() => {
                setValidationErrors([]);
                setShowInstructionsDialog(true);
                scrollToRow(validationErrors[0].tableId, validationErrors[0].errors[0]);
              }}
              table={tables.find(table => table.tableId === validationErrors[0].tableId)}
              tableEdits={tableEdits[validationErrors[0].tableId]}
              getInputDescription={table => {
                const mediaTable = table as MediaTable;
                const channel = getSegmentValue(mediaTable.channelId, customSegments || []) || '';
                const platform = getSegmentValue(mediaTable.platformId, customSegments || []) || '';
                return `${channel}${platform ? `, ${platform}` : ''}`;
              }}
              createEmptyEntry={date => ({
                date,
                impressions: undefined,
                otherUnit: undefined,
                redemptions: undefined,
                spend: undefined
              })}
              normalizeEntry={(table, entry) => {
                const mediaTable = table as MediaTable;
                const mediaEntry = entry as MediaEntry;

                if (mediaTable.hasImpressions) {
                  mediaEntry.impressions = 0;
                }

                if (mediaTable.otherUnitLabel) {
                  mediaEntry.otherUnit = 0;
                }

                if (mediaTable.hasRedemptions) {
                  mediaEntry.redemptions = 0;
                }

                if (mediaTable.isPaid) {
                  mediaEntry.spend = 0;
                }
              }}
            />
          )}
        </>
      }
      tables={
        <div
          className='tables-container-inner'
          style={getTablesContainerInnerWidth(inputType, tables)}
        >
          {tables?.map(table => (
            <InputTable
              inputType={inputType}
              table={table}
              key={table.tableId}
              deleteTable={deleteTable}
              tablesInEditMode={tablesInEditMode}
              startEditingTable={startEditingTable}
              editingUnlocked={editingUnlocked}
              edits={tableEdits[table.tableId] ?? {}}
              setEdits={(edits: EntryEditsMap) =>
                setTableEdits({ ...tableEdits, [table.tableId]: edits })
              }
              newEntries={newTableEntries[table.tableId] ?? []}
              submitting={submitting}
              setError={setError}
              filter={filter?.filter}
              headerLabels={
                <MediaTableHeaderLabels table={table} customSegments={customSegments} />
              }
              editTableSettings={openEditSettingsModal}
              addBlankDmaRow={addBlankDmaRow}
              addPastedDmaRows={addPastedDmaRows}
              deleteUncommittedEntry={deleteUncommittedEntry}
            />
          ))}
        </div>
      }
      inputType={inputType}
      updated={updated}
      isEmpty={tables.length + deletedTables.length === 0}
      fetching={fetching}
      fetchFailed={fetchFailed}
      refetch={refetch}
      submitting={submitting}
      editingUnlocked={editingUnlocked}
      setEditingUnlocked={setEditingUnlocked}
      hasUnsavedChanges={hasUnsavedChanges}
      onFilter={newFilter => {
        setFilter({ filter: newFilter });
      }}
      validateAndUpdateTables={validateAndUpdateTables}
      setShowAddInputModal={setShowAddMediaInputModal}
      setShowDiscardDialog={setShowDiscardDialog}
      setError={setError}
      download={download}
    />
  );
};
